import { MessageRouter } from "./MessageRouter";

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private router: MessageRouter;
  private reconnectTimeout: Timer | null = null;
  private heartbeatInterval: Timer | null = null;

  constructor() {
    this.router = new MessageRouter();
  }

  connect(url: string) {
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      console.log("[WS] Connected");
      this.startHeartbeat();

      // Subscribe to topics
      this.subscribe("skopa/adsb");
      this.subscribe("skopa/adsb:batch");
      this.subscribe("skopa/silo");
      this.subscribe("skopa/silo:batch");
      // 🌟 Replaces "skopa/intercept" + "skopa/intercept/accepted".
      // The accepted/rejected handshake topics were dropped entirely —
      // PrimitivesManager has no accept/reject concept (per your earlier
      // decision). If the backend still needs to ack/reject primitives,
      // that's new domain logic, not a 1:1 port of the old topics.
      this.subscribe("/primitives");
      this.subscribe("pdar");
      this.subscribe("pdar:batch");
      this.subscribe("pdar:remove");
      this.subscribe("skopa/heatmap:batch");
      // New resolution-independent probability-field raster layer
      // (PdarProbabilityFieldManager) — separate topic/consumer from
      // skopa/heatmap:batch above, which stays wired to the existing
      // point-based heatmap layer untouched. Topic is deliberately NOT
      // namespaced under "pdar/" — PDAR is just the current producer;
      // any future target-tracking source (radar, sonar, etc.) can publish
      // probability-grid:batch frames the same way.
      this.subscribe("probability-grid:batch");
      // this.subscribe("adsb:batch");
      // this.subscribe("route:update");
      // this.subscribe("geofence:add");
    };

    this.ws.onmessage = async (event) => {
      try {
        const message = JSON.parse(event.data);
        await this.router.route(message);
      } catch (error) {
        console.error("[WS] Failed to parse message:", error);
      }
    };

    this.ws.onerror = (error) => {
      console.error("[WS] Error:", error);
    };

    this.ws.onclose = () => {
      console.log("[WS] Disconnected");
      this.stopHeartbeat();
      this.reconnect(url);
    };
  }

  private startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      this.send({ type: "ping" });
    }, 30000); // 30 seconds
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
  }

  subscribe(topic: string) {
    this.send({ action: "subscribe", topic });
  }

  unsubscribe(topic: string) {
    this.send({ action: "unsubscribe", topic });
  }

  send(data: any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  private reconnect(url: string) {
    this.reconnectTimeout = setTimeout(() => {
      console.log("[WS] Reconnecting...");
      this.connect(url);
    }, 3000);
  }

  disconnect() {
    if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
    this.stopHeartbeat();
    if (this.ws) this.ws.close();
  }
}

// Singleton
let wsClient: WebSocketClient | null = null;
export function getWebSocketClient(): WebSocketClient {
  if (!wsClient) {
    wsClient = new WebSocketClient();
  }
  return wsClient;
}
